import { useMemo } from 'react'
import { useDebounce } from 'use-debounce'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useLogQueryResolutionTime } from 'lib/postHog/useLogQueryResolutionTime'
import {
  CUSTOMER_MIN_CHARS,
  INVOICE_RESULT_LIMIT,
  RESULT_LIMIT,
  SEARCH_DEBOUNCE_MS,
  SEARCH_QUERY_RESOLVED_EVENT,
  SEARCH_STALE_TIME
} from './globalSearch.constants'

export type CustomerResult = { id: string; legalName: string; domain?: string }
export type InvoiceResult = {
  id: string
  invoiceNumber?: string
  status: 'IN_PROGRESS' | 'DRAFT' | 'FINAL' | 'SENT' | 'VOIDED'
  dueDate?: string
  grossTotal?: string
  currency?: string
  customerId?: string
}

/**
 * Orchestrates the global search: debounces the query then fires both customer
 * and invoice searches once the query reaches the minimum length. Both sections
 * search simultaneously — no prefix routing needed.
 *
 * Customers are searched by legal name and by alias in parallel and merged,
 * de-duplicated by id (a customer matching both appears once).
 */
export const useGlobalSearchResults = (rawQuery: string) => {
  const [debouncedQuery] = useDebounce(rawQuery, SEARCH_DEBOUNCE_MS)
  const trimmedQuery = debouncedQuery.trim()

  const isActive = trimmedQuery.length >= CUSTOMER_MIN_CHARS

  const customersByName = useQuery(
    dashboard20240730Client.getCustomers,
    { legalName: trimmedQuery, limit: RESULT_LIMIT },
    { enabled: isActive, staleTime: SEARCH_STALE_TIME }
  )
  const customersByAlias = useQuery(
    dashboard20240730Client.getCustomers,
    { alias: trimmedQuery, limit: RESULT_LIMIT },
    { enabled: isActive, staleTime: SEARCH_STALE_TIME }
  )
  const invoicesQuery = useQuery(
    dashboardv99990101Client.getInvoicesListSummary,
    { search: trimmedQuery, limit: INVOICE_RESULT_LIMIT },
    { enabled: isActive, staleTime: SEARCH_STALE_TIME }
  )

  // Track each query's resolution time in PostHog so a slow constituent query
  // (and overall usage) is visible without instrumenting the call sites.
  useLogQueryResolutionTime({
    eventName: SEARCH_QUERY_RESOLVED_EVENT,
    isFetching: customersByName.isFetching,
    isSuccess: customersByName.isSuccess,
    metadata: { queryType: 'customersByName' }
  })
  useLogQueryResolutionTime({
    eventName: SEARCH_QUERY_RESOLVED_EVENT,
    isFetching: customersByAlias.isFetching,
    isSuccess: customersByAlias.isSuccess,
    metadata: { queryType: 'customersByAlias' }
  })
  useLogQueryResolutionTime({
    eventName: SEARCH_QUERY_RESOLVED_EVENT,
    isFetching: invoicesQuery.isFetching,
    isSuccess: invoicesQuery.isSuccess,
    metadata: { queryType: 'invoices' }
  })

  const customers = useMemo<CustomerResult[]>(
    () =>
      mergeById([
        ...(customersByName.data?.items ?? []),
        ...(customersByAlias.data?.items ?? [])
      ])
        .slice(0, RESULT_LIMIT)
        .map(item => ({
          id: item.id,
          legalName: item.legalName,
          domain: item.domain ?? undefined
        })),
    [customersByName.data?.items, customersByAlias.data?.items]
  )

  const invoices = useMemo<InvoiceResult[]>(
    () =>
      (invoicesQuery.data?.items ?? []).map(item => ({
        id: item.id,
        invoiceNumber: item.invoiceNumber,
        status: item.status,
        dueDate: item.dueDate,
        grossTotal: item.grossTotal,
        currency: item.currency,
        customerId: item.customerId
      })),
    [invoicesQuery.data?.items]
  )

  return {
    query: trimmedQuery,
    customers,
    invoices,
    isActive,
    isCustomersLoading:
      isActive && (customersByName.isFetching || customersByAlias.isFetching),
    isInvoicesLoading: isActive && invoicesQuery.isFetching
  }
}

const mergeById = <T extends { id: string }>(items: T[]): T[] =>
  Array.from(new Map(items.map(item => [item.id, item])).values())
